import classNames from 'classnames';
import { COLOR_BRAND_CYAN_60, Conditional, IconClock, LazyImage, Text } from '@consumidor-positivo/aurora';
import SuccessIcon from '@dues/assets/icons/success-check-icon.svg';
import SuccessCircleIcon from '@dues/assets/icons/success-check-circle-icon.svg';
import { DebtData } from '@common/hooks/useDebts/types';
import illuClosedAgreement from '@dues/assets/images/illustration-closed-agreement.svg';
import illuClosedAgreementMobile from '@dues/assets/images/illustration-closed-agreement-mobile.svg';
import { useDeviceSize } from '@libs/utils/hooks/useDeviceSize';

import './styles.scss';

interface HeadingSectionProps {
  debtData: DebtData;
  isFirstInstallmentPaid?: boolean;
  withoutPaymentData?: boolean;
  hasDebitAccount?: boolean;
  firstInstallmentExpDate?: string;
  isRetryError?: boolean;
}
interface DataProps {
  pretitle?: string;
  title: string;
  subtitle?: string;
}
interface HeadingSectionDataProps {
  paid: DataProps;
  pending: DataProps;
  withoutPaymentData?: DataProps;
  hasDebitAccount?: DataProps;
  retry?: DataProps;
}

const HeadingSection = ({
  debtData,
  isFirstInstallmentPaid,
  withoutPaymentData,
  hasDebitAccount,
  firstInstallmentExpDate,
  isRetryError,
}: HeadingSectionProps) => {
  const [day, month] = firstInstallmentExpDate?.split('/') || '';
  const dueDate = `${day}/${month}`;

  const { isMobile } = useDeviceSize();

  const icon = isFirstInstallmentPaid ? SuccessIcon : SuccessCircleIcon;

  const headingSectionData: HeadingSectionDataProps = {
    paid: {
      pretitle: 'Tudo certo!',
      title: 'Recebemos o seu pagamento!',
      subtitle: `Ele está sendo analisado pelo nosso time, a confirmação do pagamento do acordo
      ${!isMobile ? '<br />' : ''} pode levar até 5 dias úteis.`,
    },

    pending: {
      pretitle: 'Acordo feito com sucesso!',
      title: `Pague
        <span>
          ${debtData.paymentOpt.display.entryValue} até o dia ${dueDate}
          ${!isMobile ? '<br />' : ''}
        </span>
        para garantir as condições do acordo.`,
    },
    hasDebitAccount: {
      pretitle: 'Acordo feito com sucesso!',
      title: `O valor <span>${debtData.paymentOpt.display.entryValue}</span> será debitado da sua conta <span>no dia ${dueDate}.</span>`,
    },
    retry: {
      pretitle: 'Solicitação feita!',
      title: 'Sua solicitação de acordo foi enviada!',

      subtitle: `Agora é só aguardar, iremos entrar em contato o quanto antes
      ${!isMobile ? '<br />' : ''}através do seu e-mail registrado.`,
    },
  };

  const getHeadingDataKey = () => {
    if (isFirstInstallmentPaid) return 'paid';
    if (hasDebitAccount) return 'hasDebitAccount';
    if (isRetryError) return 'retry';
    return 'pending';
  };

  const headingDataKey = getHeadingDataKey();
  const headingData = headingSectionData[headingDataKey];

  const isTablet = window.innerWidth >= 720 && window?.innerWidth < 1024;

  const illustration =
    isMobile || isTablet ? { src: illuClosedAgreementMobile, width: 320, height: 94 } : { src: illuClosedAgreement, width: 700, height: 164 };

  const titleVariant = (withoutPaymentData || isRetryError) && !isMobile ? 'heading-medium' : 'heading-small';

  return (
    <div
      className={classNames('heading-section', {
        'heading-section__success': isFirstInstallmentPaid,
        'heading-section__debit-account': hasDebitAccount,
        'heading-section__without-payment-data': withoutPaymentData,
        'heading-section__retry': isRetryError,
      })}
    >
      <Conditional
        condition={!!hasDebitAccount}
        renderIf={<LazyImage lazy={false} src={illustration.src} width={illustration.width} height={illustration.height} />}
      />

      <div className="heading-section__container">
        <Conditional condition={!!isFirstInstallmentPaid} renderIf={<LazyImage src={icon} width={64} height={64} alt="" />} />

        <Conditional
          condition={!!isRetryError}
          renderIf={
            <div className="heading-section__retry-icon">
              <IconClock rawColor={COLOR_BRAND_CYAN_60} size="large" />
            </div>
          }
        />

        <Text color="secondary" weight="semibold">
          {headingData?.pretitle}
        </Text>

        <Text
          className={classNames({ 'heading-section__title--retry': isRetryError })}
          variant={titleVariant}
          as="span"
          weight="bold"
          dangerouslySetInnerHTML={headingData?.title}
        />

        <Conditional condition={!!headingData?.subtitle} renderIf={<Text color="secondary" dangerouslySetInnerHTML={headingData?.subtitle} />} />
      </div>
    </div>
  );
};

export default HeadingSection;
